import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

const TrendChart = ({ 
  data, 
  metrics, 
  title,
  viewGranularity = 'monthly', // weekly, monthly, quarterly
  periodType = 'monthly' // The selected period type
}) => {
  const colors = ['#0F172A', '#2563EB', '#10B981', '#F59E0B', '#F43F5E'];

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-4">
          {title}
        </h3>
        <div className="h-64 flex items-center justify-center text-slate-400">
          No trend data available
        </div>
      </div>
    );
  }

  // Format X-axis based on view granularity
  const formatXAxis = (value, index) => {
    // If data has a 'label' field, use it directly (from time-aware trends)
    if (data[index]?.label) {
      return data[index].label;
    }
    
    // Fallback to date-based formatting
    if (!value) return '';
    const date = new Date(value);
    
    switch (viewGranularity) {
      case 'weekly':
        // For weekly view, show "Week X" or "Mon DD"
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      case 'quarterly':
        // For quarterly view, show "Q1", "Q2", etc.
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        return `Q${quarter}`;
      case 'monthly':
      default:
        return date.toLocaleDateString('en-US', { month: 'short' });
    }
  };

  const formatYAxis = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value;
  };

  // Get granularity label for the subtitle
  const getGranularityLabel = () => {
    switch (periodType) {
      case 'monthly':
        return '(Weekly breakdown)';
      case 'quarterly':
        return '(Monthly breakdown)';
      case 'yearly':
        return '(Quarterly breakdown)';
      default:
        return '';
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-6" data-testid="trend-chart">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">
          {title}
        </h3>
        {periodType !== 'weekly' && (
          <span className="text-xs text-slate-400">
            {getGranularityLabel()}
          </span>
        )}
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="#E2E8F0" 
              vertical={false} 
            />
            <XAxis 
              dataKey={data[0]?.label ? "label" : "date"}
              tickFormatter={data[0]?.label ? undefined : formatXAxis}
              tick={{ fontSize: 11, fill: '#64748B' }}
              axisLine={{ stroke: '#E2E8F0' }}
              tickLine={false}
              interval={0}
              angle={data.length > 5 ? -45 : 0}
              textAnchor={data.length > 5 ? "end" : "middle"}
              height={data.length > 5 ? 60 : 30}
            />
            <YAxis 
              tickFormatter={formatYAxis}
              tick={{ fontSize: 12, fill: '#64748B' }}
              axisLine={false}
              tickLine={false}
              width={50}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#FFFFFF',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }}
              labelStyle={{ color: '#0F172A', fontWeight: 600, marginBottom: 4 }}
              cursor={{ fill: 'rgba(37, 99, 235, 0.1)' }}
              formatter={(value, name) => {
                if (value === null || value === undefined) return ['No data', name];
                if (value >= 1000000) return [`${(value / 1000000).toFixed(2)}M`, name];
                if (value >= 1000) return [`${(value / 1000).toFixed(1)}K`, name];
                return [value.toFixed(2), name];
              }}
            />
            <Legend 
              wrapperStyle={{ paddingTop: 16 }}
              iconType="rect"
              iconSize={12}
            />
            {metrics.map((metric, idx) => (
              <Bar
                key={metric}
                dataKey={metric}
                name={metric}
                fill={colors[idx % colors.length]}
                radius={[4, 4, 0, 0]}
                maxBarSize={50}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TrendChart;
